// src/components/admin/DisputeFilters.tsx
// ============================================================
// Summit League v2 — Dispute Filters
//
// Filter controls for the dispute list page.
// Supports game, season, and multi-status filtering.
// ============================================================

import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';

type DisputeStatus = 'open' | 'under_review' | 'resolved_team_a' | 'resolved_team_b' | 'resolved_draw' | 'dismissed';

interface Game {
  gameId: number;
  gameName: string;
  gameCode: string;
}

interface Season {
  seasonId: number;
  seasonName: string;
  gameId: number;
}

interface DisputeFiltersProps {
  selectedGameId: number | undefined;
  selectedSeasonId: number | undefined;
  selectedStatuses: DisputeStatus[];
  onGameChange: (gameId: number | undefined) => void;
  onSeasonChange: (seasonId: number | undefined) => void;
  onStatusChange: (statuses: DisputeStatus[]) => void;
}

const ALL_STATUSES: { value: DisputeStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'resolved_team_a', label: 'Resolved (A)' },
  { value: 'resolved_team_b', label: 'Resolved (B)' },
  { value: 'resolved_draw', label: 'Draw' },
  { value: 'dismissed', label: 'Dismissed' },
];

export default function DisputeFilters({
  selectedGameId,
  selectedSeasonId,
  selectedStatuses,
  onGameChange,
  onSeasonChange,
  onStatusChange,
}: DisputeFiltersProps) {
  const [games, setGames] = useState<Game[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);

  // Fetch available games
  useEffect(() => {
    axiosClient
      .get('/games/active')
      .then((res) => {
        const gameList = res.data?.data?.games || res.data?.games || [];
        setGames(
          gameList.map((g: any) => ({
            gameId: g.game_id || g.gameId,
            gameName: g.game_name || g.gameName,
            gameCode: g.game_code || g.gameCode,
          }))
        );
      })
      .catch(() => {});
  }, []);

  // Fetch seasons when game changes
  useEffect(() => {
    if (!selectedGameId) {
      setSeasons([]);
      return;
    }
    axiosClient
      .get(`/seasons?game_id=${selectedGameId}`)
      .then((res) => {
        const seasonList = res.data?.data?.seasons || res.data?.seasons || [];
        setSeasons(
          seasonList.map((s: any) => ({
            seasonId: s.season_id || s.seasonId,
            seasonName: s.season_name || s.seasonName,
            gameId: s.game_id || s.gameId,
          }))
        );
      })
      .catch(() => {});
  }, [selectedGameId]);

  const handleStatusToggle = (status: DisputeStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusChange(selectedStatuses.filter((s) => s !== status));
    } else {
      onStatusChange([...selectedStatuses, status]);
    }
  };

  return (
    <div className="sl-dispute-filters">
      {/* Game filter */}
      <div className="sl-dispute-filter-group">
        <label className="sl-dispute-filter-label">Game</label>
        <select
          className="sl-dispute-filter-select"
          value={selectedGameId ?? ''}
          onChange={(e) => {
            const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
            onGameChange(val);
            onSeasonChange(undefined);
          }}
        >
          <option value="">All Games</option>
          {games.map((g) => (
            <option key={g.gameId} value={g.gameId}>
              {g.gameName}
            </option>
          ))}
        </select>
      </div>

      {/* Season filter */}
      <div className="sl-dispute-filter-group">
        <label className="sl-dispute-filter-label">Season</label>
        <select
          className="sl-dispute-filter-select"
          value={selectedSeasonId ?? ''}
          onChange={(e) => {
            const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
            onSeasonChange(val);
          }}
          disabled={!selectedGameId}
        >
          <option value="">All Seasons</option>
          {seasons.map((s) => (
            <option key={s.seasonId} value={s.seasonId}>
              {s.seasonName}
            </option>
          ))}
        </select>
      </div>

      {/* Status multi-select */}
      <div className="sl-dispute-filter-group sl-dispute-filter-group--status">
        <label className="sl-dispute-filter-label">Status</label>
        <div className="sl-dispute-status-chips">
          {ALL_STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              className={`sl-dispute-status-chip ${
                selectedStatuses.includes(s.value) ? 'sl-dispute-status-chip--active' : ''
              }`}
              onClick={() => handleStatusToggle(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
